import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// VAPID public key (would be generated on the server in production)
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const { token } = useAuth();
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(false);

  // Check if push notifications are supported
  useEffect(() => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    setSupported(isSupported);
    
    if (isSupported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker and check existing subscription
  useEffect(() => {
    if (!supported) return;

    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw-push.js');
        const existingSub = await registration.pushManager.getSubscription();
        if (existingSub) {
          setSubscription(existingSub);
        }
      } catch (e) {
        console.error('Service worker registration failed:', e);
      }
    };

    checkSubscription();
  }, [supported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!supported || !token) return;

    setLoading(true);
    setError(null);

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        setError('Notification permission denied');
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      setSubscription(sub);

      // Send subscription to backend
      await axios.post(
        `${API_URL}/api/notifications/subscribe`,
        {
          endpoint: sub.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth'))))
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return sub;
    } catch (e) {
      console.error('Push subscription error:', e);
      setError(e.message || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  }, [supported, token]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription || !token) return;

    setLoading(true);
    setError(null);

    try {
      await subscription.unsubscribe();
      
      // Notify backend
      await axios.delete(
        `${API_URL}/api/notifications/subscribe`,
        { 
          params: { endpoint: subscription.endpoint },
          headers: { Authorization: `Bearer ${token}` } 
        }
      );

      setSubscription(null);
    } catch (e) {
      console.error('Push unsubscribe error:', e);
      setError(e.message || 'Failed to unsubscribe');
    } finally {
      setLoading(false);
    }
  }, [subscription, token]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    if (!token) return;

    try {
      await axios.post(
        `${API_URL}/api/notifications/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return true;
    } catch (e) {
      console.error('Test notification error:', e);
      return false;
    }
  }, [token]);

  return {
    supported,
    permission,
    subscription,
    isSubscribed: !!subscription,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
};

export default usePushNotifications;
